import { isSafeReturnPath } from "@garagem/foundation-shared/lib/auth/safe-return-path";

/**
 * What `authClient.signIn.email` resolved to.
 *
 * A normal session is `{ user, token }`. Two-factor is `{ twoFactorRedirect: true }`
 * and has no session yet. On an OAuth authorize page the provider client sends
 * `oauth_query`, and the server replaces the session JSON with
 * `{ redirect: true, url }` — the consent page or the client's registered
 * redirect URI. That payload has neither `user` nor `token`.
 */
export type EmailSignInResult =
  | { kind: "two-factor" }
  | { kind: "session" }
  | { kind: "oauth-redirect"; url: string }
  | { kind: "invalid" };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Schemes Better Auth's `SafeUrlSchema` / our DCR guard reject. Custom
 * schemes (`cursor://…`, `com.example.app:/cb`) are valid OAuth redirect
 * targets for native clients (RFC 8252 §7.1).
 */
const DANGEROUS_REDIRECT_SCHEMES = new Set([
  "javascript:",
  "data:",
  "vbscript:",
]);

/**
 * Same-origin path (consent / login continuation) or a server-chosen
 * absolute URL — http(s) or a registered custom scheme. Rejects
 * code-execution schemes and protocol-relative hosts.
 */
export function isNavigableOAuthRedirect(url: unknown): url is string {
  if (typeof url !== "string" || url.length === 0) return false;
  if (/[\u0000-\u001f\u007f\s]/.test(url)) return false;
  if (url.startsWith("//") || url.startsWith("\\\\") || url.startsWith("/\\")) {
    return false;
  }
  if (isSafeReturnPath(url)) return true;
  try {
    const parsed = new URL(url);
    return !DANGEROUS_REDIRECT_SCHEMES.has(parsed.protocol);
  } catch {
    return false;
  }
}

export function classifyEmailSignInResult(data: unknown): EmailSignInResult {
  if (!isRecord(data)) return { kind: "invalid" };
  if (data.twoFactorRedirect === true) return { kind: "two-factor" };
  if (data.user || (typeof data.token === "string" && data.token.length > 0)) {
    return { kind: "session" };
  }
  if (data.redirect === true && isNavigableOAuthRedirect(data.url)) {
    return { kind: "oauth-redirect", url: data.url };
  }
  return { kind: "invalid" };
}
